import { useState, useEffect, useMemo } from "react";
import { Plus, Search, User, Check } from "lucide-react";
import api from "@/lib/api";

export default function StepCustomer({ customer, setCustomer }) {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newForm, setNewForm] = useState({ name: "", email: "", phone: "" });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!search) return customers;
    return customers.filter((c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase())
    );
  }, [customers, search]);

  const handleCreate = async () => {
    if (!newForm.name || !newForm.email) return;
    setCreating(true);
    try {
      const { data } = await api.post("/customers", { ...newForm, status: "active" });
      setCustomer(data);
      setCustomers([data, ...customers]);
      setShowCreate(false);
    } catch { /* */ }
    setCreating(false);
  };

  if (customer) {
    return (
      <div data-testid="step-customer">
        <h2 className="crm-heading mb-6">Cliente selecionado</h2>
        <div className="crm-card p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "var(--crm-accent-subtle)", color: "var(--crm-accent)" }}>
              <User className="w-5 h-5" />
            </div>
            <div>
              <p className="font-semibold text-base" style={{ color: "var(--crm-text)" }}>{customer.name}</p>
              <p className="text-sm" style={{ color: "var(--crm-text-muted)" }}>{customer.email} {customer.phone && `· ${customer.phone}`}</p>
            </div>
          </div>
          <button onClick={() => setCustomer(null)} className="crm-btn-secondary text-sm py-2 px-4">Alterar</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="step-customer">
      <div className="flex items-center justify-between mb-6">
        <h2 className="crm-heading">Selecionar Cliente</h2>
        <button onClick={() => setShowCreate(!showCreate)} className="crm-btn-secondary text-sm">
          <Plus className="w-4 h-4" /> Novo Cliente
        </button>
      </div>

      {showCreate && (
        <div className="crm-card p-6 mb-6 space-y-4">
          <h3 className="crm-subheading">Criar novo cliente</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <input value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
              data-testid="wizard-new-customer-name" className="crm-input" placeholder="Nome *" />
            <input value={newForm.email} onChange={(e) => setNewForm({ ...newForm, email: e.target.value })}
              data-testid="wizard-new-customer-email" className="crm-input" placeholder="Email *" type="email" />
            <input value={newForm.phone} onChange={(e) => setNewForm({ ...newForm, phone: e.target.value })}
              data-testid="wizard-new-customer-phone" className="crm-input" placeholder="Telefone" />
          </div>
          <button onClick={handleCreate} disabled={creating || !newForm.name || !newForm.email}
            data-testid="wizard-create-customer-btn" className="crm-btn-primary text-sm">
            {creating ? "Criando..." : "Criar e Selecionar"}
          </button>
        </div>
      )}

      <div className="relative mb-4">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--crm-text-dim)" }} />
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          data-testid="wizard-customer-search" className="crm-input pl-10" placeholder="Buscar cliente por nome ou email..." />
      </div>

      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="text-center py-8 text-sm" style={{ color: "var(--crm-text-dim)" }}>Nenhum cliente encontrado</p>
        ) : (
          filtered.map((c) => (
            <button
              key={c.id}
              onClick={() => setCustomer(c)}
              data-testid={`wizard-customer-${c.id}`}
              className="w-full crm-card p-4 flex items-center gap-4 text-left transition-all"
              style={{ cursor: "pointer" }}
            >
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "var(--crm-accent-subtle)", color: "var(--crm-accent)" }}>
                <User className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: "var(--crm-text)" }}>{c.name}</p>
                <p className="text-xs truncate" style={{ color: "var(--crm-text-muted)" }}>{c.email}</p>
              </div>
              <span className={`crm-badge text-[10px] ${c.status === "active" ? "crm-badge-green" : "crm-badge-gray"}`}>
                {c.status === "active" ? "Ativo" : c.status}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
